import {ApiClient} from '@hconnect/apiclient'
import {spawnSuccessToast} from '@hm/fundamental'
import {useMutation, useQueryClient} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useTranslation} from 'react-i18next'

import {TasksApiPath, FEATURE_NAMESPACE} from '../../consts'
import {BulkUploadPlantActionsProblem, BulkUploadPlantActionsResult} from '../../types'

import {TasksQueryKey} from './useTasks'

// Created actions reach the tasks list asynchronously, so refetch a few times
const INTERVAL = 400
const REPEATS = 5

export const useBulkUploadPlantActions = () => {
  const queryClient = useQueryClient()
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const refetchTasks = async () => {
    const queries = queryClient.getQueriesData({queryKey: [TasksQueryKey]})
    for (let i = 0; i < REPEATS; i++) {
      await Promise.all(
        queries.map(([key]) => queryClient.refetchQueries({queryKey: key, exact: true}))
      )
      await new Promise((r) => setTimeout(r, INTERVAL))
    }
  }

  return useMutation<BulkUploadPlantActionsResult, AxiosError<BulkUploadPlantActionsProblem>, File>(
    {
      mutationFn: async (file) => {
        const formData = new FormData()
        formData.append('csvFile', file)
        // The creator is resolved from the authenticated user, so creatorId is intentionally omitted
        formData.append('isDryRun', 'false')

        const {data} = await ApiClient.axiosInstance.post<BulkUploadPlantActionsResult>(
          `${TasksApiPath.POM_QUESTIONNARIES}/actions/bulk-upload`,
          formData
        )
        return data
      },
      // Errors are shown inside the upload dialog, see getBulkUploadFailure
      onSuccess: ({createdCount}) => {
        // A header-only file succeeds without creating anything; the dialog reports it instead
        if (!createdCount) return
        spawnSuccessToast(t('pomAction.bulkUpload.message.uploadSucceeded', {count: createdCount}))
        // Not awaited so the mutation settles (and the dialog closes) while the list catches up
        void refetchTasks()
      }
    }
  )
}
